import { FC } from 'react';
import { NavigateOptions, useNavigate } from '@tanstack/react-router';
import ListItemText from '@mui/material/ListItemText';
import { Box, Divider, Drawer, List, ListSubheader, Theme, Toolbar } from '@mui/material';
import { createUseStyles } from 'react-jss';
import { Scrollbars } from 'react-custom-scrollbars-2';
import useApp from '@/hooks/use-app.hook';
import { openNewTab, pathIsExternal } from '@/lib/url-helpers';
import MenuItem from './menu-item';
import useFeature from '@/hooks/use-feature.hook';
import useIntercom from '@/hooks/use-intercom.hook';
import { ASSUME_TOAST_HEIGHT } from '../toasts/assume-role/inline';
import { SidebarSections } from './helpers';
import AppLogo from '../app-logo';
import Text from '../text';
import Icon from '../icon';

interface Props {
  width: number;
  open: boolean;
  hasAssumeBanner: boolean;
  onSidebarToggle: () => void;
}

const DIVIDER_HEIGHT = 1;

const useStyles = createUseStyles<
  string,
  { width: string | number; open: boolean; bannerHeight: number }
>((theme: Theme) => {
  const getWidthStyles = (open: boolean, width: number | string) => ({
    width: open ? width : 60,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    ...(!open && {
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9),
      },
    }),
  });
  return {
    drawer: ({ width, open, bannerHeight }) => ({
      position: 'relative',
      whiteSpace: 'nowrap',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      backgroundColor: theme.palette.navigation.background,
      borderRight: `1px solid ${theme.palette.navigation.divider}`,
      boxSizing: 'border-box',
      ...getWidthStyles(open, width),
      zIndex: theme.zIndex.appBar - 10,
      fontSize: theme.typography.body1.fontSize,
      height: `calc(100% - ${bannerHeight}px)`,
    }),
    drawerHeader: ({ open }) => ({
      marginTop: 16,
      minHeight: 'unset',
      display: 'flex',
      alignItems: 'center',
      justifyContent: open ? 'space-between' : 'center',
      padding: open && '0px 0px 0px 16px',
      flexDirection: open ? 'row' : 'column',
      gap: open ? 0 : 12,
      '& .MuiButtonBase-root': {
        width: 34,
      },
    }),
    drawerToggle: {
      padding: '4px 8px',
    },
    drawerTitle: ({ open }) => ({
      marginTop: 8,
      display: open ? 'block' : 'none',
      padding: open && '0px 16px',
      textWrap: 'nowrap',
      textOverflow: 'clip',
      color: theme.palette.secondary.main,
    }),
    sectionHeader: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
      background: 'none',
      paddingLeft: 8,
      marginBottom: 4,
      gap: 4,
      color: theme.palette.navigation.text,
      '& .MuiListItemText-root span.MuiTypography-root': {
        fontWeight: theme.typography.fontWeightMedium,
        fontSize: theme.typography.subtitle1.fontSize,
        lineHeight: 'normal',
      },
    },
    scrollContainer: {
      width: '100%',
      paddingTop: 0,
      paddingBottom: 0,
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      '& div': {
        scrollbarWidth: 'thin',
        scrollbarColor: `${theme.palette.navigation.scrollbar} transparent`,
      },
      '& div::-webkit-scrollbar': {
        width: 10,
        backgroundColor: 'transparent',
      },
      '& div::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
        '-webkit-box-shadow': 'none',
      },
      '& div::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.navigation.scrollbar,
        outline: 'none',
        borderRadius: theme.shape.borderRadius,
      },
      maskImage: 'linear-gradient(to bottom, black 95%, transparent 100%)',
    },
    scrollThumbVert: {
      backgroundColor: theme.palette.navigation.scrollbar,
      borderRadius: theme.shape.borderRadius,
    },
    scrollHideHorizontal: {
      display: 'none',
    },
    scrollView: {
      overflowX: 'hidden !important',
      height: '100%',
      padding: 8,
    },
    divider: {
      margin: '20px -8px 10px',
      height: DIVIDER_HEIGHT,
      backgroundColor: theme.palette.navigation.divider,
      width: '100%',
    },
    topDivider: {
      margin: '8px 0px 10px',
      height: DIVIDER_HEIGHT,
      backgroundColor: theme.palette.navigation.divider,
    },
    userMenu: ({ open }) => ({
      padding: open ? 15 : 0,
      transition: theme.transitions.create('padding', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      '& > .MuiFab-root': {
        boxShadow: 'none',
        background: 'none',
      },
      '& .MuiSpeedDial-actions': {
        paddingBottom: 36,
      },
    }),
  };
});

const AppSidebar: FC<Props> = ({ width, open: isDrawerOpen, hasAssumeBanner, onSidebarToggle }) => {
  const navigate = useNavigate();
  const { signedIn } = useApp();
  const { hasFeature } = useFeature();

  const { bannerSize: intercomBannerSize } = useIntercom();

  const bannerHeight = intercomBannerSize + (hasAssumeBanner ? ASSUME_TOAST_HEIGHT : 0);

  const styles = useStyles({ width, open: isDrawerOpen, bannerHeight });

  const goHomeHref = signedIn ? '/observe/user-events' : '/';

  const handleNavigate = (to: string, target = '') => {
    if (pathIsExternal(to)) {
      openNewTab(to, target);
      return;
    }
    navigate({ to } as NavigateOptions);
  };

  /*
   * Opening a sub-item menu may cause the sidebar to expand below the bottom of the viewport, but this
   * unfortunately does not trigger the custom scrollbars to appear, event though they are active. Fire
   * a window resize event to trigger the force-update function of the scrollbar component.
   *
   * There might be some unintended side-effects, but afaik there is no other solution outside forking
   * the react-custom-scrollbars-2 repo and exposing the forceUpdate function.
   */
  const handleMenuOpen = () => {
    window.dispatchEvent(new Event('resize'));
  };

  const onNavigateHome = () => {
    navigate({ to: goHomeHref });
  };

  return (
    <Drawer
      open={isDrawerOpen}
      PaperProps={{ className: styles.drawer }}
      variant="permanent"
      anchor="left"
      id="app-sidebar"
    >
      <Toolbar className={styles.drawerHeader}>
        <AppLogo size="small" align={isDrawerOpen ? 'start' : 'center'} onClick={onNavigateHome} />
        {signedIn && (
          <Icon
            name={isDrawerOpen ? 'chevron-left' : 'chevron-right'}
            onClick={onSidebarToggle}
            color="grey"
            className={styles.drawerToggle}
          />
        )}
      </Toolbar>
      <Box className={styles.drawerTitle}>
        <Text bold>AI Security Platform</Text>
      </Box>
      <Divider className={styles.topDivider} />
      <List component="nav" className={styles.scrollContainer}>
        <Scrollbars
          renderView={(props) => <div {...props} className={styles.scrollView} />}
          renderThumbVertical={(props) => <div {...props} className={styles.scrollThumbVert}></div>}
          renderTrackHorizontal={(props) => (
            <div {...props} className={styles.scrollHideHorizontal}></div>
          )}
          renderThumbHorizontal={(props) => (
            <div {...props} className={styles.scrollHideHorizontal}></div>
          )}
          autoHide={false}
        >
          {SidebarSections.map(({ title, icon, items, key }, idx) => {
            const hasTitle = Boolean(title);
            const DividerEl =
              idx < SidebarSections.length - 1 ? <Divider className={styles.divider} /> : null;

            const allowedItems = items.filter(({ feature }) => !feature || hasFeature(feature));
            const parentItems = allowedItems.filter(({ parent }) => !parent);

            return (
              <Box key={key}>
                {isDrawerOpen && hasTitle && (
                  <ListSubheader component="div" className={styles.sectionHeader} disableSticky>
                    <Icon name={icon} size="small" color="grey" />
                    <ListItemText primary={title} />
                  </ListSubheader>
                )}
                {parentItems.map((item) => {
                  const { key } = item;
                  const subItems = allowedItems.filter(({ parent }) => parent === key);

                  return (
                    <MenuItem
                      key={key}
                      item={item}
                      onNavigate={handleNavigate}
                      isDrawerOpen={isDrawerOpen}
                      subItems={subItems}
                      onOpen={handleMenuOpen}
                    />
                  );
                })}
                {DividerEl}
              </Box>
            );
          })}
        </Scrollbars>
      </List>
    </Drawer>
  );
};

export default AppSidebar;
